import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { connect, useSelector } from "react-redux";

import { MANAGEMENT_SCREEN_URL } from "../apiConfiguration";
import { AppDispatch } from "../App";
import ForbiddenIcon from "../icons/forbidden_icon.svg?react";
import QuestionMarkIcon from "../icons/questionmark_icon.svg?react";
import SettingsIcon from "../icons/settings_icon.svg?react";
import { backToStartScreen } from "../state/appState";
import { ActiveModal, setActiveModal } from "../state/mainGui";
import { getUser, getUserIsSpectator } from "../state/session";
import { getSimulationStatus } from "../state/simulation";

import styles from "./UserMenu.module.css";

interface Props {
  setSimulationToPaused: () => void;
}

const Usermenu: React.FC<Props & PropsFromDispatch> = (props) => {
  const intl = useIntl();
  const user = useSelector(getUser);
  const userIsSpectator = useSelector(getUserIsSpectator);
  const simulationStatus = useSelector(getSimulationStatus);

  return (
    <div className={styles.Menu}>
      <a href={MANAGEMENT_SCREEN_URL} rel="noopener noreferrer" target="_blank">
        <div
          className={styles.MenuRow}
          title={intl.formatMessage({
            id: "user_menu.username_tooltip",
            defaultMessage: "Go to user page",
          })}
        >
          <div>{user === null ? "" : `${user.firstName} ${user.lastName}`}</div>
        </div>
      </a>
      <hr />
      {!userIsSpectator ? (
        <div
          className={styles.MenuRow}
          title={intl.formatMessage({
            id: "user_menu.preferences_tooltip",
            defaultMessage: "Open preferences panel",
          })}
          onClick={() => {
            props.setActiveModal("PREFERENCES");
            // Changing preferences will require the simulation to pause? At least the ratelimiter requires this.
            // next line can be removed if this is not desired.
            if (simulationStatus.status === "running") props.setSimulationToPaused();
          }}
        >
          <div>
            <FormattedMessage
              id="preferences_modal.title_calculation_speed"
              defaultMessage="Calculation speed"
            />
          </div>

          <SettingsIcon />
        </div>
      ) : null}
      <a href="https://docs.ranawaterintelligence.com/" rel="noopener noreferrer" target="_blank">
        <div
          className={styles.MenuRow}
          title={intl.formatMessage({
            id: "user_menu.help_tooltip",
            defaultMessage: "Open help pages",
          })}
        >
          <div>
            <FormattedMessage id="user_menu.help" defaultMessage="Help" />
          </div>
          <QuestionMarkIcon />
        </div>
      </a>
      {userIsSpectator ? (
        <div className={styles.MenuRow} onClick={() => props.backToStartScreen()}>
          <div>
            <FormattedMessage id="user_menu.leave_session" defaultMessage="Leave session" />
          </div>
          <ForbiddenIcon />
        </div>
      ) : (
        <>
          <div
            className={styles.MenuRow}
            title={intl.formatMessage({
              id: "user_menu.quit_simulation_tooltip",
              defaultMessage: "Asks for confirmation",
            })}
            onClick={() => {
              props.setActiveModal("QUIT_SIMULATION");
              // Quit simulation will also pause the simulation
              // if the simulation is still running
              if (simulationStatus.status === "running") props.setSimulationToPaused();
            }}
          >
            <div>
              <FormattedMessage id="user_menu.quit_simulation" defaultMessage="Quit simulation" />
            </div>
            <ForbiddenIcon />
          </div>
          <div
            className={styles.MenuRow}
            title={intl.formatMessage({
              id: "user_menu.quit_simulation_tooltip",
              defaultMessage: "Asks for confirmation",
            })}
            onClick={() => {
              props.setActiveModal("RESTART_SIMULATION");
              // Quit simulation will also pause the simulation
              // if the simulation is still running
              if (simulationStatus.status === "running") props.setSimulationToPaused();
            }}
          >
            <div>
              <FormattedMessage
                id="user_menu.restart_simulation"
                defaultMessage="Restart simulation"
              />
            </div>
            <img src="./assets/images/refresh.svg" alt="Restart" />
          </div>
        </>
      )}
    </div>
  );
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    setActiveModal: (activeModal: ActiveModal) => dispatch(setActiveModal(activeModal)),
    backToStartScreen: () => dispatch(backToStartScreen()),
  };
};
type PropsFromDispatch = ReturnType<typeof mapDispatchToProps>;

export default connect(null, mapDispatchToProps)(Usermenu);
